import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/store';
import { authActions, authSelector } from '@/store/reducers/auth';
import userThunks from '@/store/thunks/user';
import { handleRoleBasedRouting } from '@/admin/utils/roleBasedRouting';
import { hasAccessToRoute } from '@/admin/config/routes';
import { ROUTES } from '@/admin/config/routes';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
//import { cognitoService } from '@/admin/services/cognito';

const UseCheckAuth = () => {
  const dispatch = useAppDispatch();
  const { userId, userRole, interfaceLanguage } = useAppSelector(authSelector);
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n } = useAdminTranslation();

  useEffect(() => {
    const abortController = new AbortController();

    let checkAuthTimer: any = null;
    let checkUserIdTimer: any = null;

    const fetchLanguages = async () => {
      await Promise.all([dispatch(userThunks.fetchLanguages()).unwrap()]);
    };

    const isAbort = () => abortController.signal.aborted;

    const checkUserId = async () => {
      if (isAbort()) {
        return;
      }

      const localStorageUserId = localStorage.getItem('userId') || '';

      if (localStorageUserId !== userId && !localStorageUserId) {
        dispatch(authActions.signOut());
        const currentPath = window.location.pathname;
        if (currentPath !== ROUTES.LOGIN) {
          navigate(ROUTES.LOGIN, { replace: true });
        }
      }
    };

    const checkAuth = async () => {
      if (isAbort()) {
        return;
      }

      if (!navigator.onLine) {
        dispatch(authActions.appLoaded());
        return;
      }

      const token = localStorage.getItem('token');
      const currentPath = window.location.pathname;
      const isLoginPage = currentPath === ROUTES.LOGIN;
      const isPublicRoute = currentPath === ROUTES.STYLE_GUIDE;

      let userId: string | null = null;
      let userRole: string = 'guest';
      let organizationId: string | null = null;
      let authFailed = false;

      if (token) {
        try {
          const user = await dispatch(userThunks.userInfo({ retryCount: 1 })).unwrap();

          if (isAbort()) {
            return;
          }

          userId = user?.data?.metadata?.id || null;
          userRole = user?.data?.metadata?.role || 'guest';
          organizationId = user?.data?.metadata?.organizationDetails?.organizationId || null;

          await fetchLanguages();

          //if (userId) {
          //  await cognitoService.checkCognitoSession();
          //
          //  if (isAbort()) {
          //    return;
          //  }
          //}
        } catch (error) {
          console.error('Failed to get userInfo:', error);
          const innerError = (error as any)?.error;
          const isNetworkError = !navigator.onLine || (innerError && !innerError.response);
          if (isNetworkError) {
            return;
          }
          const status = innerError?.response?.status;
          if (status === 401 || status === 403) {
            await dispatch(authActions.appLoaded());
            return;
          }
          authFailed = true;
          userRole = 'guest';
          dispatch(authActions.signOut());
        }
      }

      // If no token or auth failed, and user is on a protected route, redirect to login
      if ((!token || authFailed) && !isLoginPage && !isPublicRoute) {
        navigate(ROUTES.LOGIN, { replace: true });
        await dispatch(authActions.appLoaded());
        return;
      }

      // Check if logged-in user has access to current route
      if (userRole && userRole !== 'guest' && !hasAccessToRoute(currentPath, userRole)) {
        console.warn(`Access denied to ${currentPath} for role ${userRole}`);
        // Redirect to appropriate page based on role
        handleRoleBasedRouting(userRole, organizationId, navigate);
        return;
      }

      if (
        currentPath === '/' ||
        currentPath === '' ||
        (currentPath === '/login' && userRole !== 'guest')
      ) {
        handleRoleBasedRouting(userRole, organizationId, navigate);
      }

      await dispatch(authActions.appLoaded());

      if (isAbort()) {
        return;
      }

      checkUserIdTimer = setInterval(checkUserId, 1000);
    };

    checkAuthTimer = setTimeout(checkAuth, 500);

    return () => {
      abortController.abort();

      if (checkAuthTimer) {
        clearTimeout(checkAuthTimer);
        checkAuthTimer = null;
      }

      if (checkUserIdTimer) {
        clearInterval(checkUserIdTimer);
        checkUserIdTimer = null;
      }
    };
  }, [userId, location.pathname, navigate, dispatch]);

  useEffect(() => {
    if (interfaceLanguage) {
      i18n.changeLanguage(interfaceLanguage);
      const direction = ['he', 'ar'].includes(interfaceLanguage) ? 'rtl' : 'ltr';
      document.querySelector('html')?.setAttribute('dir', direction);

      const interdeal = (window as any).interdeal;
      if (interdeal) {
        interdeal.Menulang = interfaceLanguage;
        if (interdeal.SetLocale) {
          try {
            interdeal.SetLocale(interfaceLanguage);
          } catch (error) {}
        }
      }
    }
  }, [interfaceLanguage]);
};

export default UseCheckAuth;
